import React, {useState, useMemo} from "react";
import moment from "moment";
import { DateFormats } from "../../../components/constants/securspace-constants";
import { PersonAndBoxMessage } from "../../../components/messages/PersonAndBoxMessage";
import { useSnackbar } from "../../../context/snackbar-context";
import { useSnackbarDisplayOnError } from "../../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../../hooks/request/useGetBoundReportPaginated";
import { BoundTablePagination } from "../../../components/common/inputs/boundInputs";
import { AdminBookingReportVO } from "../../../types/model/valueObject/booking/AdminBookingReportVO";
import { AdminBookingSortFilter, AdminBookingSearchParams } from "../../../components/admin/booking/AdminBookingSortFilter";
import { AdminBookingCardListWithLoader } from "../../../components/admin/booking/AdminBookingCardList";
import { AdminBookingDialogWrapper } from "../../../components/admin/booking/AdminBookingDialogWrapper";
import { adminBookingsReportEndpoints as reportEndpoints } from "../../../requests/api-endpoints";
import { useReportDataSummaryCollapseByIndex}  from "../../../hooks/report/useReportDataSummaryCollapseByIndex";
import { useBoundQueryDownloadUrl } from "../../../hooks/report/useBoundQueryDownloadUrl";
import { ReportDialogSetterProps, getDefaultReportDialog, ReportDialogActionCloseProps } from "../../../interfaces";
import DownloadCSVButton from "../../../components/report/export/DownloadCSVButton";
import {
  DownloadButtonsContainer,
  ReportDataSummary,
} from "../../../components/report/redesigned";
import {
  SecurSpaceContainer,
  SecurSpaceReportContainer,
  SecurSpaceReportTitle
} from "../../../components/common";

// a default query MUST be defined outside of any components to avoid re-initialization on each render
const DEFAULT_QUERY: AdminBookingSearchParams = {
  startDate: moment().subtract(1, 'month').format(DateFormats.DAY),
  bookingNumber: '',
  buyerAccountId: '',
  supplierAccountId: '',
  locationId: '',
  status: '',
  assetTypeGroup: '',
  brokered: '',
  durationType: '',
  sortBy: "CREATION_DATE",
  sortDir: 'desc',
  page: 0,
  size: 10,
};

const AdminBookingReport = () => {
  const { snackbarShowMessage } = useSnackbar();
  // state hooks
  const [reportQuery, setReportQuery] = useBindSearchQuery(DEFAULT_QUERY);
  const [reportDialog, setReportDialog] = useState<ReportDialogSetterProps<AdminBookingReportVO>>(getDefaultReportDialog());

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error,
    doRequest,
  } =  useGetBoundReportPaginated<AdminBookingReportVO[]>({
    requestUrl: reportEndpoints.search,
    requestParams: reportQuery
  });

  const {
    collapseByIndex: expandByIndex, // TODO yf when redesigns slow down, rename this
    handleToggleCollapse: handleToggleExpand, // TODO yf same as above
    hasExpandedItems,
    setExpandedItems,
    setExpandedAll,
    handleExpandAll,
  } = useReportDataSummaryCollapseByIndex({ data: reportPayload });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: AdminBookingSearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

const handleDialogClose = (
  closeProps?: ReportDialogActionCloseProps<AdminBookingReportVO>
) => {
  switch (closeProps?.action) {
    case 'SUCCESS':
      doRequest?.();
      setReportDialog(getDefaultReportDialog());
      break;
    default:
      setReportDialog(getDefaultReportDialog());
  }
}

  const downloadUrl = useBoundQueryDownloadUrl({ endpoint: reportEndpoints.report, query: {...reportQuery, timezone: 'UTC'} });

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Booking</SecurSpaceReportTitle>
        <AdminBookingSortFilter
          query={reportQuery}
          onChange={handleFilterChange}
        />
        <ReportDataSummary
          visible={!shouldShowPersonAndBox}
          count={pageInfo?.count || 0}
          title={"All"}
          isShowCollapseButton={true}
          hasExpandedItems={hasExpandedItems}
          setExpandedItems={setExpandedItems}
          setExpandedAll={setExpandedAll}
          handleExpandAll={handleExpandAll}
          downloadButtonsSlot={
            <DownloadButtonsContainer>
              <DownloadCSVButton title='Download CSV (UTC)' reportUrl={downloadUrl} />
            </DownloadButtonsContainer>
          }
        />
        <AdminBookingCardListWithLoader
          visible={!error}
          isLoading={isLoading}
          items={reportPayload}
          expandByIndex={expandByIndex}
          onToggleExpand={handleToggleExpand}
          onOpenDialog={setReportDialog}
        />
        <PersonAndBoxMessage
          visible={shouldShowPersonAndBox }
        />
        <BoundTablePagination<AdminBookingSearchParams>
          visible={showPagination}
          pageInfo={pageInfo}
          onChange={handleFilterChange}
        />
      </SecurSpaceReportContainer>
      <AdminBookingDialogWrapper
        dialog={reportDialog}
        onClose={handleDialogClose}
      />
    </SecurSpaceContainer>
  );
};

export default AdminBookingReport;
